/**
 * Договор и счёт заказа обучения (training-order-filling; ответ заказчицы 27.10, В16): по заявке
 * менеджер прикладывает их в заказ, клиент получает уведомление и скачивает их здесь — в мастере
 * заказа «На заполнении» и на странице заказа.
 */
import type { DocumentDTO } from '@/lib/api'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { ListItem } from '@/design-system/components/data/list-item'
import { DocIcon } from '@/design-system/components/feedback/doc-icon'

function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Типы документов «Договоры» и «Счета» (seed `document_types`). */
const PAPERWORK_TYPES = new Set(['contracts', 'invoices'])

/** Договор и счёт среди документов заказа (порядок — как у сервера). */
export function paperworkDocuments(documents: DocumentDTO[]): DocumentDTO[] {
  return documents.filter((d) => PAPERWORK_TYPES.has(d.typeCode))
}

/** Пустое состояние в мастере заказа «На заполнении»: менеджер ещё готовит документы. */
export const PAPERWORK_PENDING =
  'Менеджер подготовит договор и счёт — они появятся здесь, а мы пришлём уведомление.'

/**
 * Карточка «Договор и счёт»: список с кнопкой «Скачать». Без документов — `emptyText` (если не
 * задан, карточка не выводится: на странице заказа пустой блок не нужен).
 */
export function TrainingPaperwork({
  documents,
  emptyText,
}: {
  documents: DocumentDTO[]
  emptyText?: string
}) {
  const items = paperworkDocuments(documents)
  if (items.length === 0 && !emptyText) return null
  return (
    <Card header="Договор и счёт">
      {items.length === 0 ? (
        <p className="py-2 text-txt-secondary">{emptyText}</p>
      ) : (
        items.map((d) => (
          <ListItem
            key={d.id}
            leading={<DocIcon />}
            name={d.title}
            meta={`${d.typeName} · ${formatDate(d.date)}`}
            action={
              <Button asChild size="sm" variant="secondary">
                <a href={d.downloadUrl} download>
                  Скачать
                </a>
              </Button>
            }
          />
        ))
      )}
    </Card>
  )
}
